/**
 * The invoice page's e-invoices (hoá đơn điện tử, ADR-025 §3.5): state, number, series, template,
 * lookup code and the tax authority's code of the invoice's own e-invoice and of its corrections;
 * what could not be sent and why; the buttons staff may use now (issue, retry, look the key up,
 * take the buyer again, read the tax authority's answer, files); corrections (adjust, replace,
 * cancel) asked with a reason and approved by a second person; the history. The provider and its
 * settings are the module's (Cài đặt › Module › Hoá đơn ĐT).
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api } from '../../api';
import type {
  EInvoiceAction,
  EInvoiceChange,
  EInvoiceDocument,
  EInvoiceEvent,
  EInvoiceFile,
  EInvoicePendingChange,
  EInvoiceView,
} from '../../api/areas/einvoice';
import type { Tone } from '../../components/Badge';
import { Callout, Field, Section } from '../../components/blocks';
import { Timeline } from '../../components/flow';
import { KeyValue } from '../../components/KeyValue';
import { DataError } from '../../components/DataError';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { saveBlob } from '../declarations/shared';
import { modulesUrl, useText } from '../modules/ui';
import { Amount, InvoiceRef, mutedSmall, Status, useEnumLabel } from './shared';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

const STATE_TONE: Record<string, Tone> = {
  DRAFT: 'neutral',
  ISSUING: 'pending',
  ISSUED: 'ok',
  FAILED: 'crit',
  NEEDS_RECONCILIATION: 'warn',
  REPLACED: 'info',
  CANCELLED: 'neutral',
  WITHDRAWN: 'neutral',
};
const KIND_TONE: Record<string, Tone> = { ORIGINAL: 'info', ADJUSTMENT: 'warn', REPLACEMENT: 'info' };
const AUTHORITY_TONE: Record<string, Tone> = {
  DRAFT: 'neutral',
  ISSUED: 'pending',
  SENT_TO_TAX_AUTHORITY: 'pending',
  ACCEPTED_BY_TAX_AUTHORITY: 'ok',
  REJECTED_BY_TAX_AUTHORITY: 'crit',
  ADJUSTED: 'info',
  REPLACED: 'info',
  CANCELLED: 'neutral',
};
const CANCEL_TONE: Record<string, Tone> = { QUEUED: 'pending', SENDING: 'pending', DONE: 'ok', REFUSED: 'crit', UNKNOWN: 'warn', FAILED: 'crit' };
const EVENT_TONE: Record<string, Tone> = {
  ISSUED: 'ok',
  RECONCILED_PRESENT: 'ok',
  CANCEL_DONE: 'ok',
  DELIVERED: 'ok',
  REFUSED: 'crit',
  BUILD_FAILED: 'crit',
  NOT_SENT_EXHAUSTED: 'crit',
  CANCEL_REFUSED: 'crit',
  UNKNOWN: 'warn',
  REFUSED_UNSETTLED: 'warn',
  STALE: 'warn',
  ATTENTION: 'warn',
  KEY_NOT_FOUND: 'warn',
};

/** The buttons that call at once (no reason, no approval). */
const DIRECT: readonly EInvoiceAction[] = ['ISSUE', 'RETRY', 'RECONCILE', 'REFRESH_BUYER', 'STATUS'];
const CHANGES: readonly EInvoiceChange[] = ['ADJUST_DECREASE', 'ADJUST_INFORMATION', 'REPLACE', 'CANCEL'];

type Direct = 'ISSUE' | 'RETRY' | 'RECONCILE' | 'REFRESH_BUYER' | 'STATUS';

function call(action: Direct, id: string, opts: Parameters<typeof api.issueEInvoice>[1]): Promise<{ document_id: string }> {
  switch (action) {
    case 'RECONCILE':
      return api.reconcileEInvoice(id, opts);
    case 'REFRESH_BUYER':
      return api.refreshEInvoiceBuyer(id, opts);
    case 'STATUS':
      return api.refreshEInvoiceStatus(id, opts);
    default:
      return api.issueEInvoice(id, opts);
  }
}

/** `<series>-<number>` of the provider, or a dash. */
function providerNumber(d: Pick<EInvoiceDocument, 'series' | 'invoice_no'>): string | null {
  return d.invoice_no ? `${d.series ?? ''}${d.series ? ' · ' : ''}${d.invoice_no}` : null;
}

function DocumentCard({ view, doc, onChanged }: { view: EInvoiceView; doc: EInvoiceDocument; onChanged: () => void }) {
  const { t } = useTranslation('commerce');
  const tr = t as unknown as Lookup;
  const [open, setOpen] = useState<EInvoiceChange | 'WITHDRAW' | null>(null);
  const [download, setDownload] = useState<unknown>(null);
  const direct = useWrite((action: Direct, opts) => call(action, doc.id, opts));
  const allowed = (a: EInvoiceAction) => doc.actions.includes(a) && (CHANGES.includes(a as EInvoiceChange) || a === 'WITHDRAW' ? view.can.manage : a === 'DOWNLOAD' || view.can.issue);
  const fetchFile = (file: EInvoiceFile) => {
    setDownload(null);
    api
      .downloadEInvoice(doc.id, file)
      .then((blob) => saveBlob(blob, `hoa-don-${doc.series ?? ''}-${doc.invoice_no ?? doc.id}.${file}`))
      .catch((e: unknown) => setDownload(e));
  };
  const buyer = doc.buyer;
  const credited = doc.billing_document?.document_type === 'CREDIT_NOTE' ? doc.billing_document : null;
  return (
    <div className="space-y-4 rounded-lg border p-4" data-einvoice={doc.id}>
      <div className="flex flex-wrap items-center gap-2">
        <Status group="einvoiceKind" tones={KIND_TONE} value={doc.kind} />
        <Status group="einvoiceState" tones={STATE_TONE} value={doc.state} />
        {doc.adjust_kind ? <span className={mutedSmall}>{tr(`enum.einvoiceAdjust.${doc.adjust_kind}`, { defaultValue: doc.adjust_kind })}</span> : null}
        {credited ? (
          <span className={mutedSmall}>
            {t('einvoice.carries')} <InvoiceRef invoice={{ id: doc.billing_invoice_id, series: credited.series, number: credited.number }} />
          </span>
        ) : null}
      </div>
      <p className="text-sm text-muted-foreground">
        {doc.state === 'DRAFT' && (doc.issue_mode === 'DAILY' || (doc.kind === 'ORIGINAL' && doc.attempts === 0))
          ? tr(`einvoice.draftHelp.${doc.issue_mode}`, { defaultValue: t('einvoice.stateHelp.DRAFT') })
          : tr(`einvoice.stateHelp.${doc.state}`, { defaultValue: '' })}
        {doc.state === 'DRAFT' && doc.issue_mode === 'DAILY' && doc.attempts === 0 && Date.parse(doc.next_attempt_at) > Date.now()
          ? ` ${t('einvoice.heldUntil', { at: formatDateTime(doc.next_attempt_at) })}`
          : null}
      </p>
      <KeyValue
        columns={3}
        rows={[
          [t('einvoice.field.number'), providerNumber(doc) ? <span key="n" className="font-mono">{providerNumber(doc)}</span> : null],
          [t('einvoice.field.template'), doc.template ? <span key="tp" className="font-mono">{doc.template}</span> : null],
          [t('einvoice.field.issuedAt'), doc.issued_at ? formatDateTime(doc.issued_at) : null],
          [t('einvoice.field.lookupCode'), doc.lookup_code ? <span key="lk" className="font-mono">{doc.lookup_code}</span> : null],
          [t('einvoice.field.taxAuthorityCode'), doc.tax_authority_code ? <span key="cqt" className="font-mono">{doc.tax_authority_code}</span> : null],
          [
            t('einvoice.field.authority'),
            doc.provider_status ? (
              <span key="ps" className="flex flex-wrap items-center gap-2">
                <Status group="einvoiceAuthority" tones={AUTHORITY_TONE} value={doc.provider_status} />
                {doc.status_checked_at ? <span className={mutedSmall}>{t('einvoice.checkedAt', { at: formatDateTime(doc.status_checked_at) })}</span> : null}
              </span>
            ) : null,
          ],
          [t('einvoice.field.buyer'), buyer ? `${buyer.name}${buyer.tax_code ? ` · ${t('einvoice.taxCode')} ${buyer.tax_code}` : ''}${buyer.identity_hint ? ` · ${t('einvoice.identity')} ${buyer.identity_hint}` : ''}` : null],
          [t('einvoice.field.attempts'), doc.attempts ? String(doc.attempts) : null],
          [t('einvoice.field.provider'), doc.module ? `${doc.module}/${doc.instance_key ?? ''}` : null],
          doc.reason ? [t('einvoice.field.reason'), doc.reason] : null,
          doc.content_after ? [t('einvoice.field.content'), `${doc.content_before ? `${doc.content_before} → ` : ''}${doc.content_after}`] : null,
          doc.requested_by_name ? [t('einvoice.field.people'), t('einvoice.people', { asker: doc.requested_by_name, approver: doc.approved_by_name ?? '—' })] : null,
          doc.delivery_state ? [t('einvoice.field.delivery'), tr(`enum.einvoiceDelivery.${doc.delivery_state}`, { defaultValue: doc.delivery_state })] : null,
        ]}
      />
      {doc.state === 'FAILED' || doc.state === 'NEEDS_RECONCILIATION' || doc.attention ? (
        <Callout tone={doc.state === 'FAILED' ? 'crit' : 'warn'} title={tr(`einvoice.why.${doc.state}`, { defaultValue: t('einvoice.why.other') })}>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {(doc.problems ?? []).map((p) => (
              <li key={`${p.code}/${p.field}`}>
                {tr(`einvoice.problem.${p.code}`, { defaultValue: p.code })} <span className={mutedSmall}>({p.field})</span>
              </li>
            ))}
            {doc.refusal_code ? (
              <li>
                {tr(`einvoice.refusal.${doc.refusal_code}`, { defaultValue: t('einvoice.refusal.other', { code: doc.refusal_code }) })}
                {doc.provider_code ? <span className={mutedSmall}> ({t('einvoice.providerCode', { code: doc.provider_code })})</span> : null}
              </li>
            ) : null}
            {doc.reconcile_reason ? <li>{tr(`einvoice.reconcile.${doc.reconcile_reason}`, { defaultValue: doc.reconcile_reason })}</li> : null}
            {doc.message ? <li className="break-words">{doc.message}</li> : null}
            {doc.attention ? <li>{doc.attention}</li> : null}
          </ul>
        </Callout>
      ) : null}
      {doc.cancellation ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{t('einvoice.cancellation')}</span>
          <Status group="einvoiceCancel" tones={CANCEL_TONE} value={doc.cancellation.status} />
          <span className={mutedSmall}>{doc.cancellation.reason}</span>
        </div>
      ) : null}
      {direct.state.phase === 'error' ? <WriteError error={direct.state.error} /> : null}
      {download ? <WriteError error={download} /> : null}
      <div className="flex flex-wrap gap-2">
        {DIRECT.filter(allowed).map((a) => (
          <Button
            key={a}
            type="button"
            size="sm"
            variant={a === 'ISSUE' || a === 'RETRY' || a === 'RECONCILE' ? 'default' : 'outline'}
            disabled={direct.busy}
            onClick={() => void direct.run(a as Direct).then((r) => r && onChanged())}
          >
            {tr(`einvoice.action.${a}`)}
          </Button>
        ))}
        {allowed('DOWNLOAD') ? (
          <>
            <Button type="button" size="sm" variant="outline" onClick={() => fetchFile('pdf')}>
              {t('einvoice.action.DOWNLOAD_PDF')}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => fetchFile('xml')}>
              {t('einvoice.action.DOWNLOAD_XML')}
            </Button>
          </>
        ) : null}
        {CHANGES.filter(allowed).map((c) => (
          <Button key={c} type="button" size="sm" variant="outline" onClick={() => setOpen(c)}>
            {tr(`einvoice.action.${c}`)}
          </Button>
        ))}
        {allowed('WITHDRAW') ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setOpen('WITHDRAW')}>
            {t('einvoice.action.WITHDRAW')}
          </Button>
        ) : null}
      </div>
      {doc.events.length ? (
        <details className="text-sm">
          <summary className="cursor-pointer font-medium">{t('einvoice.history', { count: doc.events.length })}</summary>
          <div className="mt-3">
            <History events={doc.events} />
          </div>
        </details>
      ) : null}
      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl [&>*]:min-w-0">
          {open === 'WITHDRAW' ? (
            <WithdrawForm doc={doc} onDone={() => { setOpen(null); onChanged(); }} />
          ) : open ? (
            <ChangeForm view={view} doc={doc} change={open} onDone={() => { setOpen(null); onChanged(); }} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function History({ events }: { events: EInvoiceEvent[] }) {
  const { t } = useTranslation('commerce');
  const tr = t as unknown as Lookup;
  return (
    <Timeline
      items={events.map((e) => ({
        id: e.id,
        title: tr(`einvoice.event.${e.event}`, { defaultValue: e.event }),
        time: formatDateTime(e.occurred_at),
        tone: EVENT_TONE[e.event] ?? 'info',
        meta: [e.actor_name ?? (e.actor_type === 'OPERATOR' ? null : t('einvoice.system')), e.code, e.provider_code ? t('einvoice.providerCode', { code: e.provider_code }) : null].filter(Boolean).join(' · ') || undefined,
        body: e.message ?? undefined,
      }))}
    />
  );
}

function ChangeForm({ view, doc, change, onDone }: { view: EInvoiceView; doc: EInvoiceDocument; change: EInvoiceChange; onDone: () => void }) {
  const { t } = useTranslation('commerce');
  const tr = t as unknown as Lookup;
  const [reason, setReason] = useState('');
  const [note, setNote] = useState(view.open_credit_notes[0]?.id ?? '');
  const [before, setBefore] = useState('');
  const [after, setAfter] = useState('');
  const send = useWrite((body: Parameters<typeof api.proposeEInvoiceChange>[1], opts) => api.proposeEInvoiceChange(doc.id, body, opts));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void send.run({
      change,
      reason,
      credit_note_id: change === 'ADJUST_DECREASE' ? note || undefined : undefined,
      content_before: change === 'ADJUST_INFORMATION' ? before || undefined : undefined,
      content_after: change === 'ADJUST_INFORMATION' ? after || undefined : undefined,
    });
  };
  if (send.state.phase === 'done') {
    return (
      <>
        <DialogHeader>
          <DialogTitle>{tr(`einvoice.action.${change}`)}</DialogTitle>
        </DialogHeader>
        <Callout tone="ok" title={t('einvoice.change.requestedTitle')}>
          {t('einvoice.change.requested')} <Link to="/admin/approvals" className="font-medium text-primary underline-offset-4 hover:underline">{t('einvoice.change.approvals')}</Link>
        </Callout>
        <div>
          <Button type="button" onClick={onDone}>
            {t('einvoice.close')}
          </Button>
        </div>
      </>
    );
  }
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{tr(`einvoice.action.${change}`)}</DialogTitle>
        <DialogDescription>{tr(`einvoice.change.help.${change}`)}</DialogDescription>
      </DialogHeader>
      {change === 'ADJUST_DECREASE' ? (
        <Field label={t('einvoice.change.creditNote')}>
          <select className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm" value={note} onChange={(e) => setNote(e.target.value)}>
            {view.open_credit_notes.map((n) => (
              <option key={n.id} value={n.id}>
                {`${n.series}-${n.number} · ${n.total} ${n.currency}`}
              </option>
            ))}
          </select>
          {view.open_credit_notes.find((n) => n.id === note) ? (
            <span className={mutedSmall}>
              <Amount amount={view.open_credit_notes.find((n) => n.id === note)?.total} currency={view.open_credit_notes.find((n) => n.id === note)?.currency} />
            </span>
          ) : null}
        </Field>
      ) : null}
      {change === 'ADJUST_INFORMATION' ? (
        <>
          <Field label={t('einvoice.change.before')} hint={t('einvoice.change.beforeHint')}>
            <Textarea value={before} onChange={(e) => setBefore(e.target.value)} maxLength={500} />
          </Field>
          <Field label={t('einvoice.change.after')}>
            <Textarea value={after} onChange={(e) => setAfter(e.target.value)} maxLength={500} required />
          </Field>
        </>
      ) : null}
      {change === 'REPLACE' || change === 'ADJUST_INFORMATION' ? <p className={mutedSmall}>{t('einvoice.change.buyerFromAccount')}</p> : null}
      <Field label={t('einvoice.change.reason')}>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} required />
      </Field>
      <Callout tone="info">{t('einvoice.change.fourEyes')}</Callout>
      {send.state.phase === 'error' ? <WriteError error={send.state.error} /> : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={send.busy || !reason.trim() || (change === 'ADJUST_DECREASE' && !note) || (change === 'ADJUST_INFORMATION' && !after.trim())}>
          {t('einvoice.change.submit')}
        </Button>
      </div>
      <span className="sr-only">{doc.id}</span>
    </form>
  );
}

function WithdrawForm({ doc, onDone }: { doc: EInvoiceDocument; onDone: () => void }) {
  const { t } = useTranslation('commerce');
  const [reason, setReason] = useState('');
  const send = useWrite((body: { reason: string }, opts) => api.withdrawEInvoice(doc.id, body, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void send.run({ reason }).then((r) => r && onDone());
      }}
    >
      <DialogHeader>
        <DialogTitle>{t('einvoice.action.WITHDRAW')}</DialogTitle>
        <DialogDescription>{t('einvoice.withdrawHelp')}</DialogDescription>
      </DialogHeader>
      <Field label={t('einvoice.change.reason')}>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} required />
      </Field>
      {send.state.phase === 'error' ? <WriteError error={send.state.error} /> : null}
      <div>
        <Button type="submit" disabled={send.busy || !reason.trim()}>
          {t('einvoice.action.WITHDRAW')}
        </Button>
      </div>
    </form>
  );
}

function PendingChange({ view, change, onChanged }: { view: EInvoiceView; change: EInvoicePendingChange; onChanged: () => void }) {
  const { t } = useTranslation('commerce');
  const tr = t as unknown as Lookup;
  const execute = useWrite((_: null, opts) => api.executeEInvoiceChange(change.approval_request_id, opts));
  const c = change.change?.change;
  const code = c ? (c.change === 'ADJUST' ? `ADJUST_${c.adjust_kind ?? 'INFORMATION'}` : c.change) : change.kind;
  return (
    <li className="space-y-2 rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{tr(`einvoice.action.${code}`, { defaultValue: code })}</span>
        <Status group="einvoiceApproval" tones={{ PENDING: 'pending', APPROVED: 'ok' }} value={change.status} />
        <span className={mutedSmall}>{t('einvoice.pending.expires', { at: formatDateTime(change.expires_at) })}</span>
      </div>
      <p>{change.reason}</p>
      <p className={mutedSmall}>
        {t('einvoice.pending.asked', { name: change.requested_by_name ?? '—', at: formatDateTime(change.requested_at) })}
        {change.decided_by_name ? ` · ${t('einvoice.pending.approved', { name: change.decided_by_name })}` : ''}
      </p>
      {execute.state.phase === 'error' ? <WriteError error={execute.state.error} /> : null}
      {change.status === 'APPROVED' && view.can.manage ? (
        <Button type="button" size="sm" disabled={execute.busy} onClick={() => void execute.run(null).then((r) => r && onChanged())}>
          {t('einvoice.pending.execute')}
        </Button>
      ) : change.status === 'PENDING' ? (
        <p className={mutedSmall}>
          {t('einvoice.pending.waiting')} <Link to="/admin/approvals" className="font-medium text-primary underline-offset-4 hover:underline">{t('einvoice.change.approvals')}</Link>
        </p>
      ) : null}
    </li>
  );
}

/** The e-invoice section of `/invoices/:invoiceId`. */
export function EInvoiceSection({ invoiceId }: { invoiceId: string }) {
  const { t } = useTranslation('commerce');
  const tr = t as unknown as Lookup;
  const label = useEnumLabel('commerce');
  const text = useText();
  const result = useApi((signal) => api.getInvoiceEInvoices(invoiceId, { signal }), [invoiceId]);
  const create = useWrite((_: null, opts) => api.createInvoiceEInvoice(invoiceId, opts));
  if (result.status === 'loading') {
    return (
      <Section title={t('einvoice.title')}>
        <Loading rows={2} />
      </Section>
    );
  }
  if (result.status === 'error') {
    return (
      <Section title={t('einvoice.title')}>
        <DataError error={result.error} onRetry={result.reload} title={t('einvoice.loadError')} />
      </Section>
    );
  }
  const v = result.data;
  const settingsLink = (
    <Link to={modulesUrl('einvoice')} className="font-medium text-primary underline-offset-4 hover:underline">
      {t('einvoice.settingsLink')}
    </Link>
  );
  const description = v.provider ? t('einvoice.description', { provider: text(v.provider.name) }) : undefined;
  return (
    <Section title={t('einvoice.title')} description={description}>
      <div className="space-y-4">
        <p className="text-sm" data-einvoice-mode={v.settings?.issue_mode ?? ''}>
          <span className="font-medium">{t('einvoice.mode.title')}</span>{' '}
          {v.settings ? (
            <>
              <span className="font-medium">{label('einvoiceMode', v.settings.issue_mode)}</span>
              <span className={mutedSmall}> · {tr(`einvoice.mode.help.${v.settings.issue_mode}`)}</span>
            </>
          ) : (
            <span className="text-muted-foreground">{t('einvoice.mode.unreadable')}</span>
          )}
        </p>
        {v.settings ? (
          <p className="text-sm" data-einvoice-delivery={v.settings.delivery}>
            <span className="font-medium">{t('einvoice.mode.delivery')}</span> {label('einvoiceDeliverySetting', v.settings.delivery)}
          </p>
        ) : null}
        {!v.provider ? (
          <Callout tone="warn" title={t('einvoice.noProviderTitle')}>
            {t('einvoice.noProvider')} {settingsLink}
          </Callout>
        ) : null}
        {v.document_type === 'CREDIT_NOTE' ? <p className="text-sm text-muted-foreground">{t('einvoice.creditNote')}</p> : null}
        {v.documents.length === 0 && v.document_type === 'INVOICE' ? <p className="text-sm text-muted-foreground">{t('einvoice.none')}</p> : null}
        {create.state.phase === 'error' ? <WriteError error={create.state.error} /> : null}
        {v.can_create && v.can.issue ? (
          <Button type="button" size="sm" disabled={create.busy} onClick={() => void create.run(null).then((r) => r && result.reload())}>
            {t('einvoice.action.CREATE')}
          </Button>
        ) : null}
        {v.documents.map((d) => (
          <DocumentCard key={d.id} view={v} doc={d} onChanged={result.reload} />
        ))}
        {v.pending_changes.length ? (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">{t('einvoice.pending.title')}</h3>
            <ul className="space-y-2">
              {v.pending_changes.map((c) => (
                <PendingChange key={c.approval_request_id} view={v} change={c} onChanged={result.reload} />
              ))}
            </ul>
          </div>
        ) : null}
        {v.open_credit_notes.length ? (
          <p className={mutedSmall}>
            {t('einvoice.openCreditNotes', { count: v.open_credit_notes.length })}{' '}
            {v.open_credit_notes.map((n, i) => (
              <span key={n.id}>
                {i ? ', ' : ''}
                <InvoiceRef invoice={n} />
              </span>
            ))}
          </p>
        ) : null}
        <p className={mutedSmall}>
          {t('einvoice.settingsHint')} {settingsLink}
        </p>
      </div>
    </Section>
  );
}
